import { useState, useEffect } from "react";
import { CheckCircle, XCircle, RefreshCw, Unlink, Clock, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Client } from "@/types/client";

interface Integration {
  platform: string;
  connected: boolean;
  accountName?: string;
  lastSync?: string;
  recordsImported?: number;
  status?: string;
  syncHistory: SyncRecord[];
}

interface SyncRecord {
  timestamp: string;
  recordsImported: number;
  status: 'success' | 'failed';
  error?: string;
}

const platformConfig = {
  ghl: {
    name: 'GoHighLevel',
    shortName: 'GHL',
    color: 'bg-orange-500',
    connectInfo: '1. Go to the "Client Integrations" tab above\n2. Find this client and click "Connect" under GoHighLevel\n3. Enter the client\'s GHL API Token (found in GHL > Settings > Business Profile > API Key)\n4. Enter their Location ID (found in GHL > Settings > Business Profile)\n5. Click Connect to verify and sync',
  },
  meta_ads: {
    name: 'Meta Ads',
    shortName: 'Meta',
    color: 'bg-blue-500',
    connectInfo: '1. Go to the "Client Integrations" tab above\n2. Find this client and click "Connect" under Meta Ads\n3. Enter a System User Token (from Meta Business Settings > Users > System Users > Generate Token with ads_read permission)\n4. Enter the Ad Account ID (starts with act_, found in Meta Business Settings > Accounts > Ad Accounts)\n5. Click Connect to verify and sync',
  },
  google_ads: {
    name: 'Google Ads',
    shortName: 'Google',
    color: 'bg-green-500',
    connectInfo: '1. Go to the "Client Integrations" tab above\n2. Find this client and click "Connect" under Google Ads\n3. A popup will open asking the client to authorize their Google Ads account\n4. After authorization, the connection is automatic\nNote: Requires Google Ads API credentials to be configured in Supabase secrets first',
  }
};

interface IntegrationsManagerProps {
  client: Client;
  onUpdate?: (clientId: string, updates: Partial<Client>) => void;
}

export function IntegrationsManager({ client, onUpdate }: IntegrationsManagerProps) {
  const [integrations, setIntegrations] = useState<Record<string, Integration>>({});
  const [loadingPlatform, setLoadingPlatform] = useState<string | null>(null);
  const [syncingPlatform, setSyncingPlatform] = useState<string | null>(null);
  const [disconnectDialog, setDisconnectDialog] = useState<{ open: boolean; platform: string | null }>({ open: false, platform: null });

  const fetchIntegrations = async () => {
    const { data } = await supabase
      .from('integrations')
      .select('provider, status, metadata, last_sync_at, sync_count, connected_at')
      .eq('client_id', client.id)
      .eq('status', 'active');

    const mapped: Record<string, Integration> = {};
    (data || []).forEach((row: any) => {
      mapped[row.provider] = {
        platform: row.provider,
        connected: row.status === 'active',
        accountName: row.metadata?.locationId
          ? `Location ${row.metadata.locationId}`
          : undefined,
        lastSync: row.last_sync_at,
        recordsImported: row.sync_count || 0,
        status: row.status,
        syncHistory: [],
      };
    });

    Object.keys(platformConfig).forEach((platform) => {
      if (!mapped[platform]) {
        mapped[platform] = { platform, connected: false, syncHistory: [] };
      }
    });

    setIntegrations(mapped);
  };

  useEffect(() => {
    fetchIntegrations();
  }, [client.id]);

  const handleConnect = async (platform: string) => {
    setLoadingPlatform(platform);
    const config = platformConfig[platform as keyof typeof platformConfig];

    try {
      if (platform === 'ghl' || platform === 'meta_ads') {
        // GHL and Meta Ads use manual token input via the Integrations tab connect modal
        toast.info(`${config.name} Connect`, {
          description: 'Use the Integrations tab to connect this platform.',
        });
        setLoadingPlatform(null);
        return;
      }

      // Google Ads uses OAuth
      const { data, error } = await supabase.functions.invoke('google-ads-oauth-start', {
        body: { clientId: client.id }
      });

      if (error) throw error;

      if (data?.authUrl) {
        window.open(data.authUrl, 'google_ads_oauth', 'width=600,height=700');
      }

      toast.success(`${config.name} Connect`, {
        description: 'OAuth authorization started. Complete the flow in the popup window.',
      });
    } catch (err: unknown) {
      toast.error('Connection Failed', {
        description: 'Failed to start connection flow.',
      });
    } finally {
      setLoadingPlatform(null);
    }
  };

  const confirmDisconnect = async () => {
    const platform = disconnectDialog.platform;
    if (!platform) return;
    const config = platformConfig[platform as keyof typeof platformConfig];

    try {
      if (platform === 'ghl') {
        const { error } = await supabase.functions.invoke('ghl-verify', {
          body: { clientId: client.id, action: 'disconnect' },
        });
        if (error) throw error;
      } else if (platform === 'meta_ads') {
        const { error } = await supabase.functions.invoke('meta-connect', {
          body: { clientId: client.id, action: 'disconnect' },
        });
        if (error) throw error;
      } else if (platform === 'google_ads') {
        // Google Ads: use google-ads-connect edge function for proper Vault cleanup
        const { error } = await supabase.functions.invoke('google-ads-connect', {
          body: { clientId: client.id, action: 'disconnect' },
        });
        if (error) throw error;
      }

      setDisconnectDialog({ open: false, platform: null });
      toast.success(`${config.name} Disconnected`, {
        description: 'Integration has been removed.',
      });

      // Refetch real state from Supabase instead of optimistic local update
      await fetchIntegrations();
    } catch (err: unknown) {
      toast.error('Error', {
        description: 'Failed to disconnect integration.',
      });
    }
  };

  const handleSync = async (platform: string) => {
    const integration = integrations[platform];
    if (!integration?.connected) {
      toast.error('Not Connected', {
        description: 'Please connect the integration first.',
      });
      return;
    }

    setSyncingPlatform(platform);
    const config = platformConfig[platform as keyof typeof platformConfig];

    toast.info(`Syncing ${config.name}...`, {
      description: 'Fetching data from the platform.',
    });

    try {
      const syncFunctionName = platform === 'ghl' ? 'ghl-sync' :
                               platform === 'meta_ads' ? 'meta-sync' :
                               platform === 'google_ads' ? 'google-ads-sync' :
                               'sync-integration';

      const { error } = await supabase.functions.invoke(syncFunctionName, {
        body: { clientId: client.id },
      });

      if (error) throw error;

      // Refetch real state from Supabase
      await fetchIntegrations();

      toast.success('Sync Complete', {
        description: `Successfully synced ${config.name} data.`,
      });
    } catch (err: unknown) {
      toast.error('Sync Failed', {
        description: `Failed to sync ${config.name} data.`,
      });
    } finally {
      setSyncingPlatform(null);
    }
  };

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    const diff = Date.now() - new Date(dateString).getTime();
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    const minutes = Math.floor(diff / 60000);
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  return (
    <>
      <div className="space-y-4">
        {(Object.keys(platformConfig) as Array<keyof typeof platformConfig>).map((platform) => {
          const config = platformConfig[platform];
          const integration = integrations[platform];
          const isConnected = integration?.connected;
          const isLoading = loadingPlatform === platform;
          const isSyncing = syncingPlatform === platform;

          return (
            <div
              key={platform}
              className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg ${config.color}/20 flex items-center justify-center`}>
                    <span className={`font-bold text-sm ${config.color.replace('bg-', 'text-')}`}>
                      {config.shortName}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{config.name}</p>
                    {isConnected ? (
                      <div className="flex items-center gap-2 text-sm text-success">
                        <CheckCircle className="w-4 h-4" />
                        <span>Connected{integration?.accountName ? `: ${integration.accountName}` : ''}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <XCircle className="w-4 h-4" />
                        <span>Not connected</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isConnected ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSync(platform)}
                        disabled={isSyncing}
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                        {isSyncing ? 'Syncing...' : 'Sync'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDisconnectDialog({ open: true, platform })}
                        className="text-destructive hover:text-destructive"
                      >
                        <Unlink className="w-4 h-4 mr-1" />
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <TooltipProvider delayDuration={100}>
                      <Tooltip defaultOpen={false}>
                        <TooltipTrigger asChild>
                          <button
                            className="p-2 rounded-lg hover:bg-surface transition-colors"
                            onClick={(e) => e.currentTarget.focus()}
                          >
                            <Info className="w-5 h-5 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[320px]" onPointerDownOutside={(e) => e.preventDefault()}>
                          <div className="text-sm space-y-1">
                            {config.connectInfo.split('\n').map((line, i) => (
                              <p key={i}>{line}</p>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>

              {/* Connection Details */}
              {isConnected && (
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>Last sync: {formatRelativeTime(integration?.lastSync)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-success">
                      <CheckCircle className="w-4 h-4" />
                      <span>{integration?.recordsImported?.toLocaleString() || 0} records imported</span>
                    </div>
                  </div>

                  {/* Recent Syncs */}
                  {integration?.syncHistory && integration.syncHistory.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Recent syncs:</p>
                      <div className="space-y-1">
                        {integration.syncHistory.slice(0, 3).map((sync, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">
                              {new Date(sync.timestamp).toLocaleString()}
                            </span>
                            <div className="flex items-center gap-2">
                              {sync.status === 'success' ? (
                                <BadgeVenture variant="success">{sync.recordsImported} records</BadgeVenture>
                              ) : (
                                <BadgeVenture variant="error">Failed</BadgeVenture>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={disconnectDialog.open} onOpenChange={(open) => {
        if (!open) setDisconnectDialog({ open: false, platform: null });
      }}>
        <DialogContent className="bg-surface border-border">
          <DialogHeader>
            <DialogTitle>Disconnect Integration</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect {disconnectDialog.platform && platformConfig[disconnectDialog.platform as keyof typeof platformConfig]?.name}? This will stop data syncing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisconnectDialog({ open: false, platform: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDisconnect}>
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
